import React from "react";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import baseUrl from "../../../../api/baseUrl";
import socket from "../../../../api/socket";
import { FIELD_LABELS, statusStyles } from "./appointmentViewShared";

type AppointmentMobileCardProps = {
  item: Record<string, any>;
  onOpenDetails: () => void;
  onStatusChange: () => void;
  onAddTreatment?: (patientId: string) => void;
};

function FieldBlock({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide block mb-1">
        {label}
      </span>
      {children}
    </div>
  );
}

export default function AppointmentMobileCard({
  item,
  onOpenDetails,
  onStatusChange,
  onAddTreatment,
}: AppointmentMobileCardProps) {
  const patientName =
    item?.patientId?.fullName || item?.bookPatientId?.fullName || "N/A";
  const doctorName =
    [item?.doctorId?.firstName, item?.doctorId?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() || "—";
  const room = String(item?.roomNumber ?? "").trim() || "—";
  const queue =
    item?.queue != null && item?.queue !== ""
      ? String(Number(item.queue))
      : "—";
  const hasPatient = Boolean(item?.patientId?.fullName && item?.patientId?._id);

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const status = e.target.value;
    if (!status || !item?._id) return;

    socket.emit("allAppointmentStatus", {
      appointmentid: item._id,
      status,
      queue: item?.queue,
      roomNumber: item?.roomNumber,
    });

    if (status === "ongoing" && item?.queue != null && item?.queue !== "") {
      try {
        localStorage.setItem(
          "nextQueue",
          String(Math.max(0, Math.floor(Number(item.queue))))
        );
      } catch {
        /* ignore */
      }
    }

    baseUrl
      .patch(
        "/appointment/statusAppointmentById",
        {},
        { headers: { appointmentid: item._id, status } }
      )
      .then(() => onStatusChange())
      .catch((err) => console.error(err));
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 p-4 space-y-3 hover:shadow-md dark:hover:bg-slate-700/50 transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-semibold text-base text-slate-900 dark:text-slate-100 truncate flex-1 min-w-0">
          {patientName}
        </h3>
        <div className="flex gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={onOpenDetails}
            className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 flex items-center justify-center hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition-colors"
            title="View appointment details"
            aria-label="View appointment details"
          >
            <RemoveRedEyeIcon sx={{ fontSize: 18 }} />
          </button>
          {hasPatient && onAddTreatment ? (
            <button
              type="button"
              onClick={() => onAddTreatment(item.patientId._id)}
              className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 flex items-center justify-center hover:bg-emerald-100 dark:hover:bg-emerald-900/60 transition-colors"
              title="Add Treatment"
              aria-label="Add Treatment"
            >
              <MedicalInformationIcon sx={{ fontSize: 18 }} />
            </button>
          ) : null}
        </div>
      </div>

      <FieldBlock label={FIELD_LABELS.doctor}>
        <p className="text-sm text-slate-700 dark:text-slate-200">{doctorName}</p>
      </FieldBlock>

      <FieldBlock label={FIELD_LABELS.dateTime}>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {item?.date?.split("T")[0] ?? "—"}
        </p>
        <span className="mt-1 inline-block px-2.5 py-1 bg-primary/10 dark:bg-primary/30 text-primary dark:text-sky-300 rounded-md text-xs font-medium">
          {item?.startTime} - {item?.endTime}
        </span>
      </FieldBlock>

      <FieldBlock label={FIELD_LABELS.location}>
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
          {item?.sessionName || "—"}
        </p>
        <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
          Room {room}
          <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
          <span className="tabular-nums">Queue {queue}</span>
        </p>
      </FieldBlock>

      <div className="grid grid-cols-2 gap-3">
        <FieldBlock label={FIELD_LABELS.status}>
          <select
            onChange={handleStatusChange}
            value={item?.status ?? ""}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 w-full ${
              statusStyles[item?.status] || statusStyles.default
            }`}
          >
            <option value="">Select</option>
            <option value="appointment">Upcoming</option>
            <option value="completed">Completed</option>
            <option value="canceled">Canceled</option>
            <option value="inqueue">In Queue</option>
            <option value="ongoing">Ongoing</option>
            <option value="noshow">No Show</option>
          </select>
        </FieldBlock>
        <FieldBlock label={FIELD_LABELS.type}>
          <span
            className={`px-2.5 py-1 rounded-md text-xs font-medium inline-block capitalize ${
              item?.type === "online"
                ? "bg-primary/10 dark:bg-primary/30 text-primary"
                : "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
            }`}
          >
            {item?.type || "N/A"}
          </span>
        </FieldBlock>
      </div>

      <FieldBlock label={FIELD_LABELS.purpose}>
        <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">
          {item?.purposeId?.purposeName || item?.bookTreatmentId?.name || "N/A"}
        </p>
      </FieldBlock>
    </div>
  );
}
